import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import cars from "../data/cars";
import { useLanguage } from "../context/LanguageContext";

function Cars() {
  const { language } = useLanguage();

  const [category, setCategory] = useState("All");
  const [transmission, setTransmission] = useState("All");

  const translations = {
    en: {
      pageLabel: "AutoRent Albania",
      title: "Cars & Prices",
      description: "Choose the vehicle that fits your trip.",
      category: "CATEGORY",
      transmission: "TRANSMISSION",

      all: "All",
      luxury: "Luxury",
      economic: "Economic",
      allGearboxes: "All Gearboxes",
      automatic: "Automat",
      manual: "Manual",

      vehicles: "vehicles",
      car: "Car",
      perDay: "per day",
      viewDetails: "View Details",
      photoSoon: "Photo coming soon",
    },

    sq: {
      pageLabel: "AutoRent Albania",
      title: "Makinat & Çmimet",
      description:
        "Zgjidh makinën që i përshtatet udhëtimit tënd.",
      category: "KATEGORIA",
      transmission: "TRANSMISIONI",

      all: "Të Gjitha",
      luxury: "Luksoze",
      economic: "Ekonomike",
      allGearboxes: "Të Gjitha",
      automatic: "Automat",
      manual: "Manual",

      vehicles: "makina",
      car: "Makina",
      perDay: "në ditë",
      viewDetails: "Shiko Detajet",
      photoSoon: "Foto së shpejti",
    },
  };

  const t = translations[language];

  const filteredCars = useMemo(() => {
    return cars.filter((car) => {
      const categoryMatches =
        category === "All" || car.category === category;

      const transmissionMatches =
        transmission === "All" ||
        car.transmission === transmission;

      return categoryMatches && transmissionMatches;
    });
  }, [category, transmission]);

  const categoryButtons = [
    {
      value: "All",
      label: t.all,
    },
    {
      value: "Luxury",
      label: t.luxury,
    },
    {
      value: "Economic",
      label: t.economic,
    },
  ];

  const transmissionButtons = [
    {
      value: "All",
      label: t.allGearboxes,
    },
    {
      value: "Automat",
      label: t.automatic,
    },
    {
      value: "Manual",
      label: t.manual,
    },
  ];

  const translateCategory = (value) => {
    if (language === "en") {
      return value;
    }

    if (value === "Luxury") {
      return "Luksoze";
    }

    if (value === "Economic") {
      return "Ekonomike";
    }

    return value;
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "50px 20px 80px",
      }}
    >
      {/* HEADER */}
      <div style={{ marginBottom: "32px" }}>
        <p
          style={{
            margin: "0 0 8px",
            color: "#777",
            fontSize: "13px",
            fontWeight: "800",
            textTransform: "uppercase",
            letterSpacing: "1.5px",
          }}
        >
          {t.pageLabel}
        </p>

        <h1
          style={{
            margin: "0 0 10px",
            fontSize: "clamp(36px, 5vw, 54px)",
            letterSpacing: "-2px",
          }}
        >
          {t.title}
        </h1>

        <p
          style={{
            margin: 0,
            color: "#666",
            fontSize: "17px",
          }}
        >
          {t.description}
        </p>
      </div>

      {/* CATEGORY FILTER */}
      <div style={{ marginBottom: "18px" }}>
        <p
          style={{
            margin: "0 0 10px",
            fontSize: "13px",
            fontWeight: "800",
            color: "#777",
          }}
        >
          {t.category}
        </p>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {categoryButtons.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setCategory(item.value)}
              style={{
                border: "1px solid #dddddd",
                padding: "11px 18px",
                borderRadius: "12px",
                cursor: "pointer",
                background:
                  category === item.value
                    ? "#111317"
                    : "#ffffff",
                color:
                  category === item.value
                    ? "#ffffff"
                    : "#111317",
                fontWeight: "800",
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* TRANSMISSION FILTER */}
      <div style={{ marginBottom: "36px" }}>
        <p
          style={{
            margin: "0 0 10px",
            fontSize: "13px",
            fontWeight: "800",
            color: "#777",
          }}
        >
          {t.transmission}
        </p>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {transmissionButtons.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() =>
                setTransmission(item.value)
              }
              style={{
                border: "1px solid #dddddd",
                padding: "11px 18px",
                borderRadius: "12px",
                cursor: "pointer",
                background:
                  transmission === item.value
                    ? "#111317"
                    : "#ffffff",
                color:
                  transmission === item.value
                    ? "#ffffff"
                    : "#111317",
                fontWeight: "800",
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* VEHICLE COUNT */}
      <p
        style={{
          marginBottom: "20px",
          color: "#777",
          fontSize: "14px",
          fontWeight: "700",
        }}
      >
        {filteredCars.length} {t.vehicles}
      </p>

      {/* CAR GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "24px",
        }}
      >
        {filteredCars.map((car) => (
          <article
            key={car.id}
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid #e8e8e8",
              boxShadow:
                "0 8px 30px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Link
              to={`/cars/${car.id}`}
              style={{
                display: "block",
              }}
            >
              <div
                style={{
                  height: "230px",
                  background: "#e9e9e9",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                {car.images &&
                car.images.length > 0 ? (
                  <img
                    src={car.images[0]}
                    alt={car.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#888",
                      fontWeight: "700",
                    }}
                  >
                    {t.photoSoon}
                  </div>
                )}
              </div>
            </Link>

            <div style={{ padding: "20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "15px",
                }}
              >
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "21px",
                    }}
                  >
                    {car.name}
                  </h2>

                  <p
                    style={{
                      margin: "5px 0 0",
                      color: "#888",
                      fontSize: "13px",
                    }}
                  >
                    {t.car} #{car.id}
                  </p>
                </div>

                <div
                  style={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  <strong
                    style={{
                      fontSize: "22px",
                    }}
                  >
                    €{car.price}
                  </strong>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#777",
                    }}
                  >
                    {t.perDay}
                  </div>
                </div>
              </div>

              {/* SPECS */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "18px",
                }}
              >
                <span
                  style={{
                    background: "#f3f3f3",
                    borderRadius: "9px",
                    padding: "7px 10px",
                    fontSize: "13px",
                  }}
                >
                  {car.engine}
                </span>

                <span
                  style={{
                    background: "#f3f3f3",
                    borderRadius: "9px",
                    padding: "7px 10px",
                    fontSize: "13px",
                  }}
                >
                  {car.transmission}
                </span>

                <span
                  style={{
                    background: "#f3f3f3",
                    borderRadius: "9px",
                    padding: "7px 10px",
                    fontSize: "13px",
                  }}
                >
                  {translateCategory(car.category)}
                </span>
              </div>

              <Link
                to={`/cars/${car.id}`}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: "18px",
                  textAlign: "center",
                  background: "#111317",
                  color: "#ffffff",
                  padding: "14px",
                  borderRadius: "12px",
                  fontWeight: "800",
                }}
              >
                {t.viewDetails}
              </Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export default Cars;